import { useMemo } from 'react'
import cn from 'classnames'
import Typography from '../../../components/Typography/Typography'
import { TYPOGRAPHY_WEIGHT } from '../../../components/Typography'
import { FieldWithLabel } from '../../../components/FieldWithLabel/FieldWithLabel'
import { AttachmentsList } from '../../../components/AttachmentsList/AttachmentsList'
import { ReactComponent as Download16 } from '../../../assets/svg/Download16.svg'
import { BTN_TXT, MAX_FILE_SIZE_30_MB } from '../../../constants'
import {
  ATTACHMENTS_FILE_TYPES,
  QUESTIONS_FILE_TYPES,
  RFP_INTENT_TO_BID_LETTER_FILE_TYPES,
  RFP_WORKBOOK_FILE_TYPES
} from '../../../components/FileUpload/constants'
import { ReactComponent as File16 } from '../../../assets/svg/File16.svg'
import { FileUpload } from '../../../components/FileUpload/FileUpload'
import { validateFileTypes } from '../../../helper/common'
import { useRFPDetailsContext } from '../RFPDetails/Providers/RFPDetailsContextProvider'
import { TRFPBiddingData } from '../RFPDetails/types'

type TBiddingFilesUploadProps = {
  isViewMode: boolean
  biddingData: TRFPBiddingData
}

export const BiddingFilesUpload = ({
  isViewMode,
  biddingData
}: TBiddingFilesUploadProps) => {
  const { actions } = useRFPDetailsContext()
  const { handleUploadVendorFiles, handleDeleteVendorAttachment } = actions

  const hasPriceFileDocs = useMemo(
    () =>
      biddingData.documents.filter((i) => i.document_type === 'price_file')
        .length > 0,
    [biddingData.documents]
  )

  const templatesNumber = useMemo(
    () =>
      biddingData.documents.filter((i) => i.document_type === 'document')
        .length,
    [biddingData.documents]
  )

  const questionsDocsNumber = useMemo(
    () =>
      biddingData.documents.filter((i) => i.document_type === 'questions')
        .length,
    [biddingData.documents]
  )

  const hasAttachments = useMemo(
    () =>
      biddingData.documents.filter((i) => i.document_type === 'attachments')
        .length > 0,
    [biddingData.documents]
  )

  const areDocsGenerated = useMemo(
    () => biddingData.owner_documents.length > 0 || isViewMode,
    [biddingData.owner_documents, isViewMode]
  )

  const communityAttachments = useMemo(
    () =>
      biddingData.owner_documents.filter(
        (i) => i.document_type === 'attachments'
      ),
    [biddingData.owner_documents]
  )

  return (
    <div className="row gap-16">
      <div className={cn('rfp-details-general__files column')}>
        <Typography.Label
          className="rfp-details-general__files__label"
          weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
        >
          Required Documentation
        </Typography.Label>
        {areDocsGenerated && (
          <>
            <FieldWithLabel
              title="RFP Workbook"
              className="rfp-details-general__files__title mt-12"
            >
              <div className="row rfp-details-general__files__wrapper">
                {!isViewMode && (
                  <AttachmentsList
                    label=""
                    className="rfp-details-general__files__wrapper__template"
                    icon={<Download16 className="attachment__icon" />}
                    names={[BTN_TXT.FORM_TO_COMPLETE]}
                    files={biddingData.owner_documents.filter(
                      (i) => i.document_type === 'price_file'
                    )}
                    onDelete={handleDeleteVendorAttachment}
                    disableActions
                    hideSeparator
                  />
                )}
                {(isViewMode || hasPriceFileDocs) && (
                  <AttachmentsList
                    label=""
                    className="rfp-details-general__files__title__file"
                    icon={
                      !isViewMode ? (
                        <File16 className="attachment__icon" />
                      ) : undefined
                    }
                    files={biddingData.documents.filter(
                      (i) => i.document_type === 'price_file'
                    )}
                    onDelete={handleDeleteVendorAttachment}
                    disableActions={isViewMode}
                    hideSeparator
                  />
                )}
                {!isViewMode && !hasPriceFileDocs && (
                  <FileUpload
                    documentType="price_file"
                    className="rfp-details-general__files__wrapper__upload"
                    uploadBtnText={BTN_TXT.UPLOAD_COMPLETED_WORKBOOK}
                    handleUploadFile={handleUploadVendorFiles}
                    uploadFilesProps={{
                      multiple: false,
                      maxSize: MAX_FILE_SIZE_30_MB,
                      onDropAccepted: (file) =>
                        handleUploadVendorFiles(file, 'price_file'),
                      accept: {
                        ...RFP_WORKBOOK_FILE_TYPES
                      },
                      validator: (file) =>
                        validateFileTypes(
                          RFP_WORKBOOK_FILE_TYPES,
                          file,
                          MAX_FILE_SIZE_30_MB
                        )
                    }}
                  />
                )}
              </div>
            </FieldWithLabel>
            <FieldWithLabel
              title="Intent to Bid Letter"
              className="rfp-details-general__files__title  mt-12"
            >
              <div className="row rfp-details-general__files__wrapper">
                {!isViewMode && (
                  <AttachmentsList
                    label=""
                    className="rfp-details-general__files__wrapper__template"
                    icon={<Download16 className="attachment__icon" />}
                    names={[BTN_TXT.FORM_TO_COMPLETE]}
                    files={biddingData.owner_documents.filter(
                      (i) => i.document_type === 'document'
                    )}
                    disableActions
                  />
                )}
                {templatesNumber > 0 && (
                  <AttachmentsList
                    label={''}
                    className="rfp-details-general__files__title__file"
                    icon={<File16 className="attachment__icon" />}
                    files={biddingData.documents.filter(
                      (i) => i.document_type === 'document'
                    )}
                    onDelete={handleDeleteVendorAttachment}
                    disableActions={isViewMode}
                    hideSeparator
                  />
                )}
                {!isViewMode && templatesNumber < 1 && (
                  <FileUpload
                    documentType="document"
                    className="rfp-details-general__files__wrapper__upload"
                    uploadBtnText={BTN_TXT.UPLOAD_COMPLETED_BID_LETTER}
                    handleUploadFile={handleUploadVendorFiles}
                    uploadFilesProps={{
                      multiple: false,
                      maxSize: MAX_FILE_SIZE_30_MB,
                      onDropAccepted: (file) =>
                        handleUploadVendorFiles(file, 'document'),
                      accept: {
                        ...RFP_INTENT_TO_BID_LETTER_FILE_TYPES
                      },
                      validator: (file) =>
                        validateFileTypes(
                          RFP_INTENT_TO_BID_LETTER_FILE_TYPES,
                          file,
                          MAX_FILE_SIZE_30_MB
                        )
                    }}
                  />
                )}
              </div>
            </FieldWithLabel>
            <FieldWithLabel
              title="General Questionnaire"
              className="rfp-details-general__files__title without-border mt-12"
            >
              <div className="row rfp-details-general__files__wrapper">
                {!isViewMode && (
                  <AttachmentsList
                    label=""
                    names={[BTN_TXT.FORM_TO_COMPLETE]}
                    className="rfp-details-general__files__wrapper__template"
                    icon={<Download16 className="attachment__icon" />}
                    files={biddingData.owner_documents.filter(
                      (i) => i.document_type === 'questions'
                    )}
                    disableActions
                  />
                )}
                {(questionsDocsNumber > 0 || isViewMode) && (
                  <AttachmentsList
                    label={''}
                    className="rfp-details-general__files__title__file"
                    icon={
                      !isViewMode ? (
                        <File16 className="attachment__icon" />
                      ) : undefined
                    }
                    files={biddingData.documents.filter(
                      (i) => i.document_type === 'questions'
                    )}
                    onDelete={handleDeleteVendorAttachment}
                    disableActions={isViewMode}
                    hideSeparator
                  />
                )}
                {!isViewMode && questionsDocsNumber < 1 && (
                  <FileUpload
                    documentType="questions"
                    className="rfp-details-general__files__wrapper__upload"
                    uploadBtnText={BTN_TXT.UPLOAD_COMPLETED_QUESTIONNAIRE}
                    handleUploadFile={handleUploadVendorFiles}
                    uploadFilesProps={{
                      multiple: false,
                      maxSize: MAX_FILE_SIZE_30_MB,
                      onDropAccepted: (file) =>
                        handleUploadVendorFiles(file, 'questions'),
                      accept: {
                        ...QUESTIONS_FILE_TYPES
                      },
                      validator: (file) =>
                        validateFileTypes(
                          QUESTIONS_FILE_TYPES,
                          file,
                          MAX_FILE_SIZE_30_MB
                        )
                    }}
                  />
                )}
              </div>
            </FieldWithLabel>
            <div className="row gap-24">
              {!!communityAttachments.length && (
                <AttachmentsList
                  label="Sourcing owner attachments"
                  className="mt-16 rfp-details-general__files__title__attachments"
                  files={communityAttachments}
                  onDelete={handleDeleteVendorAttachment}
                  disableActions
                />
              )}
              <div>
                {(!isViewMode || hasAttachments) && (
                  <AttachmentsList
                    label="Attachments (Optional)"
                    className="mt-16 rfp-details-general__files__title__attachments"
                    icon={!isViewMode ? <File16 className="" /> : undefined}
                    files={biddingData.documents.filter(
                      (i) => i.document_type === 'attachments'
                    )}
                    onDelete={handleDeleteVendorAttachment}
                    disableActions={isViewMode}
                  />
                )}
                {!isViewMode &&
                  biddingData.documents.filter(
                    (i) => i.document_type === 'attachments'
                  ).length < 10 && (
                    <FileUpload
                      documentType="attachments"
                      uploadBtnText="Upload"
                      handleUploadFile={handleUploadVendorFiles}
                      uploadFilesProps={{
                        multiple: false,
                        maxSize: MAX_FILE_SIZE_30_MB,
                        onDropAccepted: (file) =>
                          handleUploadVendorFiles(file, 'attachments'),
                        accept: {
                          ...ATTACHMENTS_FILE_TYPES
                        },
                        validator: (file) =>
                          validateFileTypes(
                            ATTACHMENTS_FILE_TYPES,
                            file,
                            MAX_FILE_SIZE_30_MB
                          )
                      }}
                    />
                  )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
